import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { LoadingContext, MButton, MInputTextField } from "src/M_Components";
import { Paginator } from "primereact/paginator";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import MUploadFiles from "src/M_Components/M_UploadFiles/M_UploadFiles";
import TableHeader from "../../components/tableHelpers/TableHeader";
import ColumnMenu from "../../components/tableHelpers/ColumnMenu";
import ViewsMenu from "../../components/tableHelpers/ViewMenu";
import { AdvanceFilter } from "src/M_Components/M_Table/advanceFilter/AdvanceFilter";
import {
  headerCellStyle,
  subHeaderCellStyle,
  cellStyle,
} from "../../components/tableHelpers/TableStyleUtils";
import { isFailurePayload, apiMessage } from "src/components/utils/apiResponse";
import { useTableFilters } from "../../components/tableHelpers/useTableFilters";

/* ---------------------------------------------------------------------------
   Small helpers
   --------------------------------------------------------------------------- */

/* Keeps bid input as a decimal: digits plus at most one dot. */
const normalizeBidInput = (raw) => {
  if (raw === "" || raw == null) return "";
  let s = String(raw).replace(",", ".");
  s = s.replace(/[^0-9.]/g, "");
  const parts = s.split(".");
  if (parts.length > 2) s = `${parts[0]}.${parts.slice(1).join("")}`;
  return s;
};

/* Keeps loads input as an integer: digits only. */
const normalizeLoadsInput = (raw) => {
  if (raw === "" || raw == null) return "";
  return String(raw).replace(/[^\d]/g, "");
};

/* Keeps ETD input as an integer: digits only. */
const normalizeEtdInput = (raw) => {
  if (raw === "" || raw == null) return "";
  return String(raw).replace(/[^\d]/g, "");
};

/* Blocks scientific notation and signs in numeric fields. */
const blockExpSigns = (e) => {
  if (["e", "E", "+", "-"].includes(e.key)) e.preventDefault();
};

/* Blocks decimals and signs in integer-only fields. */
const blockNonIntegerKeys = (e) => {
  if (["e", "E", "+", "-", ".", ","].includes(e.key)) e.preventDefault();
};

/* Sizes Excel columns based on the longest content. */
const autoFitColumns = (worksheet) => {
  (worksheet.columns || []).forEach((column) => {
    let max = 10;
    column.eachCell?.({ includeEmpty: true }, (cell) => {
      const v = cell.value == null ? "" : String(cell.value);
      max = Math.max(max, v.length + 2);
    });
    column.width = max;
  });
};

/* Decodes base64 that comes from the upload component. */
const base64ToArrayBuffer = (dataUrlOrBase64) => {
  const base64 = (dataUrlOrBase64 || "").includes(",")
    ? dataUrlOrBase64.split(",")[1]
    : dataUrlOrBase64;
  const binary = atob(base64);
  const len = binary.length;
  const buf = new ArrayBuffer(len);
  const view = new Uint8Array(buf);
  for (let i = 0; i < len; i++) view[i] = binary.charCodeAt(i);
  return buf;
};

/* ---------------------------------------------------------------------------
   Component: InviteTableSection
   --------------------------------------------------------------------------- */

export default function InviteTableSection({
  token,
  mode, // "spot" | "phase"
  lanes, // list of lanes from the API (each has origin/destination info)
  crudMode, // "create" | "edit" | "view"
  readOnly = false,
  prefillQuotes = [], // existing quotes in edit/view
  submitServices, // { spotBatch, phaseBatch }
  editServices, // { spot: { create, update }, phase: { create, update } }
  tenderId,
  tenderPhaseId,
  carrierId,
  tenderLoads = 0,
  acceptedLoadsByLane = {},
  onRefresh,
  onBatchSubmitSuccess,
}) {
  const { t } = useTranslation();
  const ctx = useContext(LoadingContext);
  const isCreateMode = crudMode === "create";
  const isEditMode = crudMode === "edit";
  const isViewMode = crudMode === "view";

  /* -------------------------------------------------------------------------
     Local state
     ------------------------------------------------------------------------- */

  // Normalised lanes used by the grid: one row per lane
  const [rows, setRows] = useState([]); // [{ laneId, origin, destination }]

  // Baseline values coming from the server (used to restore lines in edit mode)
  const [baseline, setBaseline] = useState({}); // laneId -> { bidValue, loads, estimatedTransitDays }

  // QuoteId per lane (used in edit mode to decide between create and update)
  const [quoteIdByLane, setQuoteIdByLane] = useState({}); // laneId -> quoteId

  // Current draft shown in the grid (always as strings)
  const [draft, setDraft] = useState({}); // laneId -> { bidValue, loads, estimatedTransitDays }

  // Status per lane in create mode (ok/error for feedback)
  const [status, setStatus] = useState({}); // laneId -> { ok: true } | { error: string }

  // In edit mode only one lane is editable at a time
  const [activeLaneId, setActiveLaneId] = useState(null);

  // Global submitting flag
  const [submitting, setSubmitting] = useState(false);

  // Column visibility
  const ALL_COLUMNS = [
    "Origin",
    "Destination",
    "BidValue",
    "Loads",
    "EstimatedTransitDays",
  ];
  const [selectedColumns, setSelectedColumns] = useState(ALL_COLUMNS);

  // Pagination
  const [first, setFirst] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Toolbar popover anchors
  const [anchorEls, setAnchorEls] = useState(null);
  const [anchorAF, setAnchorAF] = useState(null);
  const [anchorV, setAnchorV] = useState(null);

  // Excel importer panel
  const [showImporter, setShowImporter] = useState(false);

  // ===============================================================================================
  // Advanced filters + saved views boilerplate
  // ===============================================================================================
  const {
    metaPair,
    views,
    addMetaPair,
    deleteFilter,
    deleteAllFilters,
    submitFilters,
    resetToDefault,
    applyView,
    saveView,
  } = useTableFilters({
    // When filters are submitted, reset pagination
    onSubmit: () => setFirst(0),
  });

  /* -------------------------------------------------------------------------
     Tracks the maximum allowed loads per lane and flags loads that exceed capacity
     ------------------------------------------------------------------------- */

  const maxLoadsByLane = useMemo(() => {
    const cap = Number(tenderLoads) || 0;
    const map = {};
    (rows || []).forEach((r) => {
      const laneId = r?.laneId;
      if (!laneId) return;
      const accepted = Number(acceptedLoadsByLane?.[laneId] ?? 0) || 0;
      const remaining = Math.max(0, cap - accepted);
      map[laneId] = remaining;
    });
    return map;
  }, [rows, tenderLoads, acceptedLoadsByLane]);

  const loadsExceedByLane = useMemo(() => {
    const out = {};
    Object.entries(draft || {}).forEach(([k, v]) => {
      const laneId = Number(k);
      if (!laneId) return;

      const max = Number(maxLoadsByLane?.[laneId] ?? 0);
      const valStr = v?.loads;
      const val = Number(String(valStr ?? ""));
      if (!Number.isFinite(val) || valStr === "" || valStr == null) return;

      if (max > 0 && val > max) out[laneId] = true;
      if (max === 0 && val > 0) out[laneId] = true;
    });
    return out;
  }, [draft, maxLoadsByLane]);

  /* -------------------------------------------------------------------------
     Effect: normalise lanes coming from the API
     ------------------------------------------------------------------------- */
  useEffect(() => {
    if (!lanes || lanes.length === 0) {
      setRows([]);
      setBaseline({});
      setDraft({});
      setQuoteIdByLane({});
      setStatus({});
      setActiveLaneId(null);
      setFirst(0);
      return;
    }

    const normalized = (lanes || []).map((l) => {
      // API (/flat): laneId is the real lane identifier we need for quoting
      const laneId =
        l?.laneId ?? l?.LaneId ?? l?.laneID ?? l?.Id ?? l?.id ?? null;

      // API (/flat): sourceLocation / destinationLocation
      const origin =
        l?.sourceLocation ??
        l?.SourceLocation ??
        l?.Origin ??
        l?.origin ??
        l?.Source ??
        l?.source ??
        l?.LaneSource ??
        l?.laneSource ??
        "";

      const destination =
        l?.destinationLocation ??
        l?.DestinationLocation ??
        l?.Destination ??
        l?.destination ??
        l?.DestinationName ??
        l?.destinationName ??
        l?.LaneDestination ??
        l?.laneDestination ??
        "";

      return {
        laneId: laneId != null ? Number(laneId) : null,
        origin: String(origin || "").trim(),
        destination: String(destination || "").trim(),
      };
    });

    // Only keep lanes with all required fields
    const filtered = normalized.filter(
      (r) => r.laneId && r.origin && r.destination
    );

    // Stable visual ordering
    filtered.sort((a, b) => {
      const d = a.destination.localeCompare(b.destination);
      return d !== 0 ? d : a.origin.localeCompare(b.origin);
    });

    // only rows are updated here. Quotes are handled by prefillQuotes effect.
    setRows(filtered);
    setFirst(0);
  }, [lanes]);

  /* -------------------------------------------------------------------------
     Effect: inject prefill quotes into baseline and draft
     ------------------------------------------------------------------------- */

  useEffect(() => {
    if (!prefillQuotes || prefillQuotes.length === 0) {
      if (isEditMode || isViewMode) {
        setBaseline({});
        setDraft({});
        setQuoteIdByLane({});
        setActiveLaneId(null);
        setStatus({});
      }
      return;
    }

    const base = {};
    const ids = {};

    for (const q of prefillQuotes) {
      const laneId = Number(q?.LaneId ?? q?.laneId ?? null);
      if (!laneId) continue;

      const bidRaw = q?.BidValue ?? q?.bidValue;
      const loadsRaw = q?.Loads ?? q?.loads;
      const etdRaw =
        q?.EstimatedTransitDays ?? q?.estimatedTransitDays ?? q?.etd;

      base[laneId] = {
        bidValue: bidRaw != null ? String(bidRaw) : "",
        loads: loadsRaw != null ? String(loadsRaw) : "",
        estimatedTransitDays: etdRaw != null ? String(etdRaw) : "",
      };

      const quoteId = q?.QuoteId ?? q?.Id ?? q?.id;
      if (quoteId != null) ids[laneId] = Number(quoteId);
    }

    setBaseline(base);
    setDraft(base);
    setQuoteIdByLane(ids);
    setActiveLaneId(null);
    setStatus({});
  }, [prefillQuotes, isEditMode, isViewMode]);

  /* -------------------------------------------------------------------------
     Advanced filter configuration and helpers
     ------------------------------------------------------------------------- */

  const metaType = useMemo(
    () => [
      {
        metaName: "origin",
        metaDisplay: "Origin",
        metaType: "string",
        metaIsList: false,
      },
      {
        metaName: "destination",
        metaDisplay: "Destination",
        metaType: "string",
        metaIsList: false,
      },
    ],
    []
  );

  /* -------------------------------------------------------------------------
     Filtering + pagination
     ------------------------------------------------------------------------- */

  const filteredRows = useMemo(() => {
    let data = [...rows];

    metaPair.forEach((filter) => {
      const value = String(filter.value || "").toLowerCase();
      if (!value) return;

      if (filter.name === "origin") {
        if (filter.operation === "Contains") {
          data = data.filter((r) => r.origin.toLowerCase().includes(value));
        }
        if (filter.operation === "Equals") {
          data = data.filter((r) => r.origin.toLowerCase() === value);
        }
      }

      if (filter.name === "destination") {
        if (filter.operation === "Contains") {
          data = data.filter((r) =>
            r.destination.toLowerCase().includes(value)
          );
        }
        if (filter.operation === "Equals") {
          data = data.filter((r) => r.destination.toLowerCase() === value);
        }
      }
    });

    return data;
  }, [rows, metaPair]);

  const pagedRows = useMemo(
    () => filteredRows.slice(first, first + rowsPerPage),
    [filteredRows, first, rowsPerPage]
  );

  /* -------------------------------------------------------------------------
     Helper: is a given lane editable in the current state?
     ------------------------------------------------------------------------- */

  const isDisabledCell = useCallback(
    (laneId) => {
      if (!laneId || submitting || readOnly) return true;
      if (isCreateMode && status[laneId]?.ok) return true;
      if (isEditMode) return laneId !== activeLaneId;
      return false;
    },
    [submitting, readOnly, isCreateMode, isEditMode, status, activeLaneId]
  );

  /* -------------------------------------------------------------------------
     Draft change handlers
     ------------------------------------------------------------------------- */

  const onChangeBid = (laneId, value) =>
    setDraft((prev) => ({
      ...prev,
      [laneId]: {
        ...(prev[laneId] || {}),
        bidValue: normalizeBidInput(value),
      },
    }));

  const onChangeLoads = (laneId, value) =>
    setDraft((prev) => ({
      ...prev,
      [laneId]: {
        ...(prev[laneId] || {}),
        loads: normalizeLoadsInput(value),
      },
    }));

  const onChangeEstimatedTransitDays = (laneId, value) =>
    setDraft((prev) => ({
      ...prev,
      [laneId]: {
        ...(prev[laneId] || {}),
        estimatedTransitDays: normalizeEtdInput(value),
      },
    }));

  /* -------------------------------------------------------------------------
     Edit mode: activate a single lane for editing
     ------------------------------------------------------------------------- */

  const activateLane = (laneId) => {
    if (!isEditMode || readOnly) return;
    if (!laneId) return;

    // Clicking the same lane again keeps the current edits
    if (laneId === activeLaneId) return;

    // Switching to another lane discards local edits and restores baseline
    setDraft({ ...baseline });
    setStatus({});
    setActiveLaneId(laneId);
  };

  /* -------------------------------------------------------------------------
     Change detection
     ------------------------------------------------------------------------- */

  const hasAnyDraftValues = useMemo(
    () =>
      Object.values(draft).some(
        (v) =>
          (v?.bidValue != null && v.bidValue !== "") ||
          (v?.loads != null && v.loads !== "") ||
          (v?.estimatedTransitDays != null && v.estimatedTransitDays !== "")
      ),
    [draft]
  );

  const hasActiveChanges = useMemo(() => {
    if (!isEditMode || !activeLaneId) return false;

    const current = draft[activeLaneId] || {
      bidValue: "",
      loads: "",
      estimatedTransitDays: "",
    };

    const original = baseline[activeLaneId] || {
      bidValue: "",
      loads: "",
      estimatedTransitDays: "",
    };

    return (
      (current.bidValue ?? "") !== (original.bidValue ?? "") ||
      (current.loads ?? "") !== (original.loads ?? "") ||
      (current.estimatedTransitDays ?? "") !==
      (original.estimatedTransitDays ?? "")
    );
  }, [isEditMode, activeLaneId, draft, baseline]);

  /* -------------------------------------------------------------------------
     Validation helpers
     ------------------------------------------------------------------------- */

  // In create mode, walks through all lanes and builds a clean payload
  const validateBatch = () => {
    const errors = [];
    const payload = [];

    for (const [key, value] of Object.entries(draft)) {
      const laneId = Number(key);
      if (!laneId) continue;

      const bidStr = value?.bidValue;
      const loadsStr = value?.loads;
      const etdStr = value?.estimatedTransitDays;

      const hasBid = bidStr !== "" && bidStr != null;
      const hasLoads = loadsStr !== "" && loadsStr != null;
      const hasEtd = etdStr !== "" && etdStr != null;

      // Completely empty lane: ignored
      if (!hasBid && !hasLoads && !hasEtd) continue;

      // Partially filled lane: considered invalid
      if (!hasBid || !hasLoads || !hasEtd) {
        errors.push(t("AllQuoteFieldsRequired"));
        continue;
      }

      const bidNum = Number(String(bidStr).replace(",", "."));
      const loadsNum = Number(String(loadsStr));
      const etdNum = Number(String(etdStr));

      if (!Number.isFinite(bidNum) || bidNum <= 0) {
        errors.push(t("InvalidBidValue"));
        continue;
      }

      if (
        !Number.isFinite(loadsNum) ||
        !Number.isInteger(loadsNum) ||
        loadsNum <= 0
      ) {
        errors.push(t("InvalidLoadsValue"));
        continue;
      }

      if (
        !Number.isFinite(etdNum) ||
        !Number.isInteger(etdNum) ||
        etdNum <= 0
      ) {
        errors.push(t("InvalidEstimatedTransitDays"));
        continue;
      }

      // This validation prevents submitting loads that exceed the remaining capacity for the lane.
      const maxForLane = Number(maxLoadsByLane?.[laneId] ?? 0);
      if (loadsNum > maxForLane) {
        errors.push(t("LoadsExceedRemainingCapacity"));
        continue;
      }

      payload.push({
        laneId,
        bidValue: bidNum,
        loads: loadsNum,
        estimatedTransitDays: etdNum,
      });
    }

    return { errors, payload };
  };

  // In edit mode, validates the active lane
  const validateSingleLane = (laneId) => {
    const errors = [];
    const value = draft[laneId] || {};

    const bidStr = value?.bidValue;
    const loadsStr = value?.loads;
    const etdStr = value?.estimatedTransitDays;

    const hasBid = bidStr !== "" && bidStr != null;
    const hasLoads = loadsStr !== "" && loadsStr != null;
    const hasEtd = etdStr !== "" && etdStr != null;

    if (!hasBid || !hasLoads || !hasEtd) {
      errors.push(t("AllQuoteFieldsRequired"));
      return { errors, payload: null };
    }

    const bidNum = Number(String(bidStr).replace(",", "."));
    const loadsNum = Number(String(loadsStr));
    const etdNum = Number(String(etdStr));

    if (!Number.isFinite(bidNum) || bidNum <= 0) {
      errors.push(t("InvalidBidValue"));
    }

    if (
      !Number.isFinite(loadsNum) ||
      !Number.isInteger(loadsNum) ||
      loadsNum <= 0
    ) {
      errors.push(t("InvalidLoadsValue"));
    }

    if (!Number.isFinite(etdNum) || !Number.isInteger(etdNum) || etdNum <= 0) {
      errors.push(t("InvalidEstimatedTransitDays"));
    }

    // This validation prevents saving loads that exceed the remaining capacity for the lane.
    const maxForLane = Number(maxLoadsByLane?.[laneId] ?? 0);
    if (Number.isFinite(loadsNum) && loadsNum > 0 && loadsNum > maxForLane) {
      errors.push(t("LoadsExceedRemainingCapacity"));
    }

    if (errors.length) return { errors, payload: null };

    return {
      errors,
      payload: {
        laneId,
        bidValue: bidNum,
        loads: loadsNum,
        estimatedTransitDays: etdNum,
      },
    };
  };

  /* -------------------------------------------------------------------------
     Submit handler
     ------------------------------------------------------------------------- */
  const onSubmitAll = async () => {
    // Read-only mode or missing mode means nothing to do
    if (readOnly || !mode) return;

    /* -----------------------------------------------------------------------
       CREATE MODE: batch submit
       ----------------------------------------------------------------------- */
    if (isCreateMode) {
      const { errors, payload } = validateBatch();

      if (errors.length) {
        ctx.toastMessages("error", "", errors.join(" - "));
        return;
      }

      if (payload.length === 0) {
        ctx.toastMessages("warn", "", t("NothingToSubmit"));
        return;
      }

      // Global overlay while the async request is in flight (MRouter LoadingContext)
      ctx.setIsLoad(true);
      setSubmitting(true);

      try {
        const svc =
          mode === "spot"
            ? submitServices?.spotBatch
            : submitServices?.phaseBatch;

        if (!svc || typeof svc.post !== "function") {
          ctx.toastMessages("error", "", t("SubmitServiceNotConfigured"));
          setSubmitting(false);
          return;
        }

        // Follows the contract where the adapter injects tenderId /
        // tenderPhaseId and carrierId. This function only sends the items.
        const request = token ? { token, items: payload } : { items: payload };

        const res = await svc.post(request);

        if (isFailurePayload(res) || res?.IsFailure) {
          ctx.toastMessages(
            "error",
            "",
            apiMessage(res, t("ErrorSaving") || "ErrorSaving")
          );
          return;
        }

        const nextStatus = { ...status };
        for (const item of payload) {
          nextStatus[item.laneId] = { ok: true };
        }
        setStatus(nextStatus);

        ctx.toastMessages("success", "", t("SubmissionCompleted"));

        await onRefresh?.();
        if (typeof onBatchSubmitSuccess === "function") {
          onBatchSubmitSuccess();
        }
      } catch (err) {
        ctx.toastMessages(
          "error",
          "",
          apiMessage(err, t("ErrorSaving") || "ErrorSaving")
        );
      } finally {
        setSubmitting(false);
        ctx.setIsLoad(false);
      }

      return;
    }

    /* -----------------------------------------------------------------------
       EDIT MODE: create or update a single quote
       ----------------------------------------------------------------------- */
    if (isEditMode) {
      if (!activeLaneId) {
        ctx.toastMessages("warn", "", t("SelectRowToEdit"));
        return;
      }

      const { errors, payload } = validateSingleLane(activeLaneId);
      if (errors.length || !payload) {
        ctx.toastMessages("error", "", errors.join(" - "));
        return;
      }

      if (!carrierId) {
        ctx.toastMessages("error", "", t("MissingCarrierId"));
        return;
      }

      ctx.setIsLoad(true);
      setSubmitting(true);

      try {
        const svcGroup =
          mode === "spot" ? editServices?.spot : editServices?.phase;

        if (!svcGroup) {
          ctx.toastMessages("error", "", t("EditServiceNotConfigured"));
          setSubmitting(false);
          return;
        }

        const existingQuoteId = quoteIdByLane[activeLaneId];
        let res;

        if (existingQuoteId) {
          // Updates an existing quote for this lane
          const body = {
            bidValue: payload.bidValue,
            loads: payload.loads,
            estimatedTransitDays: payload.estimatedTransitDays,
          };

          res = await svcGroup.update(existingQuoteId, body);
        } else {
          // Creates a new quote for this lane in edit mode
          if (mode === "spot" && !tenderId) {
            ctx.toastMessages("error", "", t("MissingTenderId"));
            setSubmitting(false);
            return;
          }

          if (mode === "phase" && !tenderPhaseId) {
            ctx.toastMessages("error", "", t("MissingTenderPhaseId"));
            setSubmitting(false);
            return;
          }

          const baseBody = {
            laneId: payload.laneId,
            bidValue: payload.bidValue,
            loads: payload.loads,
            estimatedTransitDays: payload.estimatedTransitDays,
            carrierId: Number(carrierId),
          };

          // IMPORTANT:
          // - Spot: only send tenderId
          // - Phase: always send tenderPhaseId and, if available, also tenderId
          const body =
            mode === "spot"
              ? {
                ...baseBody,
                tenderId: Number(tenderId),
              }
              : {
                ...baseBody,
                ...(tenderId != null ? { tenderId: Number(tenderId) } : {}),
                tenderPhaseId: Number(tenderPhaseId),
              };

          res = await svcGroup.create(body);
        }

        if (isFailurePayload(res) || res?.IsFailure) {
          ctx.toastMessages(
            "error",
            "",
            apiMessage(res, t("ErrorSaving") || "ErrorSaving")
          );
          return;
        }

        ctx.toastMessages("success", "", t("SubmissionCompleted"));

        await onRefresh?.();

        // After a successful submit, refreshed data replaces local edits
        setActiveLaneId(null);
        setStatus({});
      } catch (err) {
        ctx.toastMessages(
          "error",
          "",
          apiMessage(err, t("ErrorSaving") || "ErrorSaving")
        );
      } finally {
        setSubmitting(false);
        ctx.setIsLoad(false);
      }
    }
  };

  /* -------------------------------------------------------------------------
     Flags used by the UI
     ------------------------------------------------------------------------- */
  const canSubmitCreate =
    isCreateMode &&
    !submitting &&
    !readOnly &&
    !!lanes?.length &&
    hasAnyDraftValues;

  const canSubmitEdit =
    isEditMode &&
    !submitting &&
    !readOnly &&
    !!activeLaneId &&
    hasActiveChanges;

  const canClearCreate =
    isCreateMode && !submitting && hasAnyDraftValues && !readOnly;

  const canClearEdit =
    isEditMode &&
    !submitting &&
    !readOnly &&
    !!activeLaneId &&
    hasActiveChanges;

  /* -------------------------------------------------------------------------
     Refresh helper (toolbar)
     ------------------------------------------------------------------------- */
  const handleRefresh = async () => {
    // Global overlay while refreshing data
    ctx.setIsLoad(true);
    try {
      await onRefresh?.();
      setFirst(0);
      setStatus({});
      setActiveLaneId(null);
    } finally {
      ctx.setIsLoad(false);
    }
  };

  /* -------------------------------------------------------------------------
     Excel export: one row per lane with Origin/Destination/Bid/Loads/ETD
     ------------------------------------------------------------------------- */
  const handleExport = async () => {
    ctx.setIsLoad(true);
    try {
      if (!filteredRows.length) {
        ctx.toastMessages("warn", "", t("NothingToExport"));
        return;
      }

      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet("CarrierInvite");

      // Header row
      ws.addRow([
        t("Origin"),
        t("Destination"),
        t("BidValue"),
        t("Loads"),
        t("EstimatedTransitDays"),
      ]);

      ws.getRow(1).font = { bold: true };
      ws.getRow(1).alignment = {
        vertical: "middle",
        horizontal: "center",
      };
      ws.views = [{ state: "frozen", ySplit: 1, xSplit: 0 }];

      // Data rows
      filteredRows.forEach((lane) => {
        const laneDraft = draft[lane.laneId] || {};
        const bid = laneDraft.bidValue ?? "";
        const loads = laneDraft.loads ?? "";
        const etd = laneDraft.estimatedTransitDays ?? "";

        ws.addRow([
          lane.origin,
          lane.destination,
          bid === "" ? null : Number(bid),
          loads === "" ? null : Number(loads),
          etd === "" ? null : Number(etd),
        ]);
      });

      // Lock everything by default
      ws.eachRow((r) =>
        r.eachCell((c) => {
          c.protection = { locked: true };
        })
      );

      // In create mode, numeric fields remain editable in Excel
      if (!readOnly && isCreateMode) {
        for (let r = 2; r <= ws.rowCount; r++) {
          const bidCell = ws.getCell(r, 3);
          const loadsCell = ws.getCell(r, 4);
          const etdCell = ws.getCell(r, 5);

          bidCell.protection = { locked: false };
          loadsCell.protection = { locked: false };
          etdCell.protection = { locked: false };

          bidCell.dataValidation = {
            type: "decimal",
            operator: "greaterThan",
            formula1: 0,
            allowBlank: true,
            showInputMessage: true,
            promptTitle: t("BidValue"),
            prompt: t("EnterPositiveDecimal"),
          };

          // The Excel template prevents values that exceed the remaining capacity for each lane.
          const rowLaneId = filteredRows?.[r - 2]?.laneId;
          const maxForLane = Number(maxLoadsByLane?.[rowLaneId] ?? 0);

          loadsCell.dataValidation = {
            type: "whole",
            operator: "between",
            formula1: 1,
            formula2: Math.max(1, maxForLane),
            allowBlank: true,
            showInputMessage: true,
            promptTitle: t("Loads"),
            prompt: t("EnterPositiveInteger"),
          };

          etdCell.dataValidation = {
            type: "whole",
            operator: "greaterThan",
            formula1: 0,
            allowBlank: true,
            showInputMessage: true,
            promptTitle: t("EstimatedTransitDays"),
            prompt: t("EnterPositiveInteger"),
          };
        }
      }

      autoFitColumns(ws);

      await ws.protect("carrier_invite", {
        selectLockedCells: true,
        selectUnlockedCells: !readOnly && isCreateMode,
      });

      const buffer = await wb.xlsx.writeBuffer();

      const fileName =
        mode === "spot"
          ? readOnly || isEditMode
            ? "SpotQuotes.xlsx"
            : "CarrierInviteTemplate.xlsx"
          : readOnly || isEditMode
            ? "PhaseQuotes.xlsx"
            : "CarrierInviteTemplate.xlsx";

      saveAs(new Blob([buffer]), fileName);
      ctx.toastMessages("success", "", t("Excel exported"));
    } catch {
      ctx.toastMessages("error", "", t("Error exporting Excel"));
    } finally {
      ctx.setIsLoad(false);
    }
  };

  /* -------------------------------------------------------------------------
     Excel import: only used in create mode (batch)
     ------------------------------------------------------------------------- */
  const handleFilesChange = async (files) => {
    ctx.setIsLoad(true);
    try {
      if (!files || !files.length) return;

      const file = files[0];
      const name = (file?.name || "").toLowerCase();

      if (!name.endsWith(".xlsx")) {
        ctx.toastMessages("error", "", t("PleaseUploadXlsx"));
        return;
      }

      const ab = base64ToArrayBuffer(file.base64);
      const wb = new ExcelJS.Workbook();
      await wb.xlsx.load(ab);

      const ws = wb.getWorksheet("CarrierInvite") || wb.worksheets[0];
      if (!ws) {
        ctx.toastMessages("error", "", t("InvalidExcelNoWorksheet"));
        return;
      }

      const headerRow = ws.getRow(1);
      const headerMap = {};
      headerRow.eachCell((cell, col) => {
        const key = String(cell.value || "").trim().toLowerCase();
        if (key) headerMap[key] = col;
      });

      // Supports both raw English keys and the translated text used in the UI
      const originKey = (t("Origin") || "Origin").trim().toLowerCase();
      const destinationKey = (t("Destination") || "Destination")
        .trim()
        .toLowerCase();
      const bidValueKey = (t("BidValue") || "BidValue").trim().toLowerCase();
      const bidKey = (t("Bid") || "Bid").trim().toLowerCase();
      const loadsKey = (t("Loads") || "Loads").trim().toLowerCase();
      const etdKey = (t("EstimatedTransitDays") || "EstimatedTransitDays")
        .trim()
        .toLowerCase();

      const originCol = headerMap["origin"] ?? headerMap[originKey];
      const destinationCol =
        headerMap["destination"] ?? headerMap[destinationKey];
      const bidCol =
        headerMap["bidvalue"] ??
        headerMap["bid"] ??
        headerMap[bidValueKey] ??
        headerMap[bidKey];
      const loadsCol = headerMap["loads"] ?? headerMap[loadsKey];
      const etdCol =
        headerMap["estimatedtransitdays"] ??
        headerMap["etd"] ??
        headerMap[etdKey];

      if (!originCol || !destinationCol || !bidCol || !loadsCol || !etdCol) {
        ctx.toastMessages("error", "", t("InvalidTemplateHeader"));
        return;
      }

      const toNum = (val) => {
        if (val == null || val === "") return null;
        if (typeof val === "number") return val;
        const n = Number(String(val).replace(",", "."));
        return Number.isFinite(n) ? n : null;
      };

      // Builds a helper map to quickly find lanes by origin + destination
      const laneIndex = new Map(); // "origin|||destination" -> laneId
      rows.forEach((l) => {
        const key = `${l.origin}|||${l.destination}`;
        if (!laneIndex.has(key)) {
          laneIndex.set(key, l.laneId);
        }
      });

      const updates = {};

      for (let r = 2; r <= ws.rowCount; r++) {
        const originRaw = ws.getCell(r, originCol).value;
        const destRaw = ws.getCell(r, destinationCol).value;

        if (!originRaw && !destRaw) continue;

        const origin = String(originRaw || "").trim();
        const destination = String(destRaw || "").trim();
        if (!origin || !destination) continue;

        const key = `${origin}|||${destination}`;
        const laneId = laneIndex.get(key);
        if (!laneId) continue;

        const bidVal = toNum(ws.getCell(r, bidCol).value);
        const loadsVal = toNum(ws.getCell(r, loadsCol).value);
        const etdVal = toNum(ws.getCell(r, etdCol).value);

        // Completely empty row: ignored
        if (bidVal == null && loadsVal == null && etdVal == null) continue;

        // All three values must be present and valid
        if (
          bidVal == null ||
          loadsVal == null ||
          etdVal == null ||
          !Number.isFinite(bidVal) ||
          !Number.isFinite(loadsVal) ||
          !Number.isFinite(etdVal) ||
          !Number.isInteger(loadsVal) ||
          !Number.isInteger(etdVal) ||
          bidVal <= 0 ||
          loadsVal <= 0 ||
          etdVal <= 0
        ) {
          ctx.toastMessages(
            "warning",
            "",
            `${t("InvalidValuesAt")} ${origin} - ${destination}`
          );
          continue;
        }

        // Imported loads are capped by the remaining capacity for the lane.
        const maxForLane = Number(maxLoadsByLane?.[laneId] ?? 0);
        if (loadsVal > maxForLane) {
          ctx.toastMessages(
            "warning",
            "",
            `${t("LoadsExceedRemainingCapacity")} ${origin} - ${destination}`
          );
          continue;
        }

        updates[laneId] = {
          bidValue: String(bidVal),
          loads: String(loadsVal),
          estimatedTransitDays: String(etdVal),
        };
      }

      if (Object.keys(updates).length === 0) {
        ctx.toastMessages("warn", "", t("NoValidDataInExcel"));
        return;
      }

      setDraft((prev) => {
        const next = { ...prev };
        Object.entries(updates).forEach(([laneId, v]) => {
          next[laneId] = {
            ...(next[laneId] || {}),
            bidValue: normalizeBidInput(v.bidValue),
            loads: normalizeLoadsInput(v.loads),
            estimatedTransitDays: normalizeEtdInput(v.estimatedTransitDays),
          };
        });
        return next;
      });

      ctx.toastMessages("success", "", t("ExcelImportedIntoGrid"));
      setShowImporter(false);
    } catch {
      ctx.toastMessages("error", "", t("ErrorReadingExcel"));
    } finally {
      ctx.setIsLoad(false);
    }
  };

  /* -------------------------------------------------------------------------
     Simple read-only cell component to keep layout aligned with inputs
     ------------------------------------------------------------------------- */
  const ViewCell = ({ value, align = "center" }) => (
    <div
      className="cip-viewcell"
      style={{
        padding: "6px 8px",
        minHeight: 36,
        display: "flex",
        alignItems: "center",
        justifyContent:
          align === "center"
            ? "center"
            : align === "right"
              ? "flex-end"
              : "flex-start",
        fontWeight: 500,
      }}
    >
      {value === "" || value == null ? "-" : String(value)}
    </div>
  );

  /* -------------------------------------------------------------------------
     Column menu configuration (no groups, just single columns)
     ------------------------------------------------------------------------- */
  const singleColumns = useMemo(
    () => [
      "Origin",
      "Destination",
      "BidValue",
      "Loads",
      "EstimatedTransitDays",
    ],
    []
  );
  const columnGroups = useMemo(() => [], []);

  /* -------------------------------------------------------------------------
     Render
     ------------------------------------------------------------------------- */
  return (
    <>
      {/* Main actions: Submit and Clear */}
      <div className="cip-main-table-div">
        <div className="cip-actions">
          {!readOnly && (
            <MButton
              label={t("Submit")}
              icon={
                <i
                  className="pi pi-send iconTable"
                  style={{ color: "var(--color-green-light)" }}
                />
              }
              onClick={onSubmitAll}
              disabled={submitting || (!canSubmitCreate && !canSubmitEdit)}
            />
          )}

          {!readOnly && (
            <MButton
              label={t("Clear")}
              icon={
                <i
                  className="pi pi-times iconTable"
                  style={{ color: "var(--color-red)" }}
                />
              }
              onClick={() => {
                if (isEditMode) {
                  // In edit mode this resets all lines back to baseline
                  setDraft({ ...baseline });
                  setStatus({});
                  setActiveLaneId(null);
                } else {
                  // In create mode this clears every draft value
                  setDraft({});
                  setStatus({});
                }
              }}
              disabled={submitting || (!canClearCreate && !canClearEdit)}
            />
          )}
        </div>

        <div className="cip-section">
          {/* Toolbar: refresh, export/import, views, filters and column visibility */}
          <TableHeader
            filterCount={metaPair.length}
            onRefresh={handleRefresh}
            onExport={handleExport}
            onImport={
              !readOnly && isCreateMode
                ? () => setShowImporter((v) => !v)
                : undefined
            }
            onOpenViews={(e) => setAnchorV(e.currentTarget)}
            onOpenFilters={(e) => setAnchorAF(e.currentTarget)}
            onOpenColumns={(e) => setAnchorEls(e.currentTarget)}
          />

          {/* Import panel (create mode only) */}
          {!readOnly && isCreateMode && showImporter && (
            <div className="cip-import">
              <div style={{ marginBottom: 8, color: "var(--color-grey)" }}>
                {t("UploadTheExcelTemplateFilledWithQuotesOnlyXlsx")}
              </div>
              <MUploadFiles
                multipleFiles={false}
                nameURL="carrier-invite-excel"
                onFilesChange={handleFilesChange}
              />
            </div>
          )}

          {/* Column visibility popover */}
          <ColumnMenu
            anchorEl={anchorEls}
            open={Boolean(anchorEls)}
            onClose={() => setAnchorEls(null)}
            singleColumns={singleColumns}
            columnGroups={columnGroups}
            selectedColumns={selectedColumns}
            onChangeSelected={setSelectedColumns}
          />

          {/* Saved views popover */}
          <ViewsMenu
            anchorEl={anchorV}
            open={Boolean(anchorV)}
            onClose={() => setAnchorV(null)}
            viewsMap={views}
            onSelectDefault={() => {
              resetToDefault();
              setAnchorV(null);
            }}
            onSelectView={(view) => {
              applyView(view);
              setAnchorV(null);
            }}
            onCreateNewViewClick={() => {
              setAnchorV(null);
              setAnchorAF(document.getElementById("advFilter"));
            }}
          />

          {/* Advanced filter dialog (Origin + Destination) */}
          <AdvanceFilter
            metaPair={metaPair}
            metaType={metaType}
            onAddMetaPair={addMetaPair}
            onSubmitFilters={submitFilters}
            onDeleteFilter={deleteFilter}
            onDeleteAllFilters={deleteAllFilters}
            anchorAF={anchorAF}
            setAnchorAF={setAnchorAF}
            onSaveFilter={(viewName) => {
              saveView(viewName);
            }}
            views={views}
            onToasts={() => { }}
            color="var(--color-red)"
            removeFilters={["Add"]}
          />

          {/* Main table: one lane per row */}
          <div className="cip-table-wrapper">
            <table className="cip-table">
              <thead>
                <tr>
                  {selectedColumns.includes("Origin") && (
                    <th
                      style={{
                        ...headerCellStyle(),
                        textAlign: "center",
                        minWidth: 160,
                      }}
                    >
                      {t("Origin")}
                    </th>
                  )}
                  {selectedColumns.includes("Destination") && (
                    <th
                      style={{
                        ...headerCellStyle(),
                        textAlign: "center",
                        minWidth: 160,
                      }}
                    >
                      {t("Destination")}
                    </th>
                  )}
                  {selectedColumns.includes("BidValue") && (
                    <th className="cip-col-bid" style={headerCellStyle()}>
                      {t("BidValue")}
                    </th>
                  )}
                  {selectedColumns.includes("Loads") && (
                    <th className="cip-col-loads" style={headerCellStyle()}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <div>{t("Loads")}</div>
                      </div>
                    </th>
                  )}
                  {selectedColumns.includes("EstimatedTransitDays") && (
                    <th className="cip-col-etd" style={headerCellStyle()}>
                      {t("EstimatedTransitDays")}
                    </th>
                  )}
                </tr>
              </thead>

              <tbody>
                {pagedRows.map((lane) => {
                  const laneId = lane.laneId;
                  const laneDraft = draft[laneId] || {};
                  const bidValue = laneDraft.bidValue ?? "";
                  const loadsValue = laneDraft.loads ?? "";
                  const etdValue = laneDraft.estimatedTransitDays ?? "";
                  const disabled = isDisabledCell(laneId);

                  const maxForLane = Number(maxLoadsByLane?.[laneId] ?? 0);
                  const exceedsLoads = !!loadsExceedByLane?.[laneId];

                  const numericCellClass =
                    !readOnly && !isViewMode && laneId
                      ? "cip-cell cip-cell-clickable"
                      : "cip-cell";

                  return (
                    <tr key={laneId}>
                      {selectedColumns.includes("Origin") && (
                        <td
                          style={{
                            ...subHeaderCellStyle(),
                            fontWeight: 500,
                          }}
                        >
                          {lane.origin}
                        </td>
                      )}

                      {selectedColumns.includes("Destination") && (
                        <td
                          style={{
                            ...subHeaderCellStyle(),
                            fontWeight: 500,
                          }}
                        >
                          {lane.destination}
                        </td>
                      )}

                      {selectedColumns.includes("BidValue") && (
                        <td
                          className={`${numericCellClass} cip-col-bid`}
                          style={cellStyle()}
                          onClick={() => activateLane(laneId)}
                        >
                          {laneId ? (
                            readOnly || isViewMode ? (
                              <ViewCell value={bidValue} />
                            ) : isEditMode && activeLaneId !== laneId ? (
                              <ViewCell value={bidValue} />
                            ) : (
                              <MInputTextField
                                type="number"
                                label=""
                                value={bidValue}
                                onChange={(e) =>
                                  onChangeBid(laneId, e.target.value)
                                }
                                onKeyDown={blockExpSigns}
                                disabled={disabled}
                              />
                            )
                          ) : null}
                        </td>
                      )}

                      {selectedColumns.includes("Loads") && (
                        <td
                          className={`${numericCellClass} cip-col-loads`}
                          style={cellStyle()}
                          onClick={() => activateLane(laneId)}
                        >
                          {laneId ? (
                            readOnly || isViewMode ? (
                              <ViewCell value={loadsValue} />
                            ) : isEditMode && activeLaneId !== laneId ? (
                              <ViewCell value={loadsValue} />
                            ) : (
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 4,
                                }}
                                className={
                                  exceedsLoads ? "cip-input-exceeds" : undefined
                                }
                                title={
                                  exceedsLoads
                                    ? `${t("LoadsExceedRemainingCapacity")} (${t("Max") || "Max"
                                    }: ${maxForLane})`
                                    : ""
                                }
                              >
                                <MInputTextField
                                  type="number"
                                  label=""
                                  value={loadsValue}
                                  placeholder={`${t("Max") || "Max"}: ${maxForLane}`}
                                  onChange={(e) =>
                                    onChangeLoads(laneId, e.target.value)
                                  }
                                  onKeyDown={blockNonIntegerKeys}
                                  disabled={disabled}
                                  placeholderColor="var(--color-red-light)"
                                />
                              </div>
                            )
                          ) : null}
                        </td>
                      )}

                      {selectedColumns.includes("EstimatedTransitDays") && (
                        <td
                          className={`${numericCellClass} cip-col-etd`}
                          style={cellStyle()}
                          onClick={() => activateLane(laneId)}
                        >
                          {laneId ? (
                            readOnly || isViewMode ? (
                              <ViewCell value={etdValue} />
                            ) : isEditMode && activeLaneId !== laneId ? (
                              <ViewCell value={etdValue} />
                            ) : (
                              <MInputTextField
                                type="number"
                                label=""
                                value={etdValue}
                                onChange={(e) =>
                                  onChangeEstimatedTransitDays(
                                    laneId,
                                    e.target.value
                                  )
                                }
                                onKeyDown={blockNonIntegerKeys}
                                disabled={disabled}
                              />
                            )
                          ) : null}

                          {/* In create mode, shows per-lane status under the last numeric cell */}
                          {!readOnly &&
                            isCreateMode &&
                            laneId &&
                            (status[laneId]?.ok || status[laneId]?.error) && (
                              <div
                                className={`cip-status ${status[laneId]?.ok
                                  ? "cip-status-ok"
                                  : "cip-status-error"
                                  }`}
                              >
                                {status[laneId]?.ok
                                  ? t("Submitted")
                                  : status[laneId].error}
                              </div>
                            )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Paginator */}
          <div style={{ marginTop: 20 }}>
            <Paginator
              first={first}
              rows={rowsPerPage}
              totalRecords={filteredRows.length}
              rowsPerPageOptions={[10, 20, 50]}
              onPageChange={(e) => {
                setFirst(e.first);
                setRowsPerPage(e.rows);
              }}
              template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            />
          </div>
        </div>
      </div>
    </>
  );
}

InviteTableSection.propTypes = {
  token: PropTypes.string,
  mode: PropTypes.oneOf(["spot", "phase"]),
  lanes: PropTypes.array,
  crudMode: PropTypes.oneOf(["create", "edit", "view"]).isRequired,
  readOnly: PropTypes.bool,
  prefillQuotes: PropTypes.array,
  submitServices: PropTypes.object,
  editServices: PropTypes.object,
  tenderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tenderPhaseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  carrierId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tenderLoads: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  acceptedLoadsByLane: PropTypes.object,
  onRefresh: PropTypes.func,
  onBatchSubmitSuccess: PropTypes.func,
};
